var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useStateWithMutators } from '@rad/hooks';
import { requireToken } from '../../../utility/general';
import { postObjectJson, getJson } from '../../../utility/request';
import { TRANSACTION_STATUS } from '../../../utility/constants';
import CErrors from '../errors';
import { scrollToTop } from '@rad/utility/form';
const PAYMENT_TIMEOUT_MS = 120 * 1000;
const POLL_INTERVAL_MS = 5 * 1000;
export default (props) => {
    const token = requireToken('merchant');
    const [state, mutators] = useStateWithMutators({
        forms: {
            processing: {
                isLoading: false,
                error: null,
            },
        },
    });
    const pollRef = React.useRef(null);
    const timeoutRef = React.useRef(null);
    const consecutiveErrorsRef = React.useRef(0);
    const paymentIdRef = React.useRef(null);
    const POLL_ERROR_THRESHOLD = 3;
    const handleNavigateToStep = React.useCallback((step) => {
        props.onNavigateToStep(step !== null && step !== void 0 ? step : props.view.currentStep + 1);
    }, [props]);
    const pollForPaymentProcessing = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!paymentIdRef.current)
                throw new Error('Missing payment ID.');
            const res = yield getJson(`/merchants/${token.merchant.id}/billings/payment/status/${paymentIdRef.current}`);
            consecutiveErrorsRef.current = 0;
            if ((res === null || res === void 0 ? void 0 : res.payment_status) === TRANSACTION_STATUS.PROCESSED) {
                if (pollRef.current)
                    clearInterval(pollRef.current);
                if (timeoutRef.current)
                    clearTimeout(timeoutRef.current);
                mutators.forms.processing.isLoading(false);
                handleNavigateToStep(3);
            }
            else if ((res === null || res === void 0 ? void 0 : res.payment_status) === TRANSACTION_STATUS.DECLINED || (res === null || res === void 0 ? void 0 : res.payment_status) === TRANSACTION_STATUS.FAILED) {
                if (pollRef.current)
                    clearInterval(pollRef.current);
                if (timeoutRef.current)
                    clearTimeout(timeoutRef.current);
                mutators.forms.processing.isLoading(false);
                mutators.forms.processing.error([
                    { field: '', message: 'Payment method declined. Please try again with a different payment method.' },
                ]);
            }
        }
        catch (error) {
            consecutiveErrorsRef.current += 1;
            if (consecutiveErrorsRef.current < POLL_ERROR_THRESHOLD) {
                return;
            }
            if (pollRef.current)
                clearInterval(pollRef.current);
            if (timeoutRef.current)
                clearTimeout(timeoutRef.current);
            mutators.forms.processing.isLoading(false);
            mutators.forms.processing.error([
                { field: '', message: 'Unable to verify payment status. Please try again.' },
            ]);
        }
    }), [token.merchant.id, handleNavigateToStep, mutators.forms.processing.isLoading]);
    React.useEffect(() => {
        mutators.forms.processing.isLoading(true);
        mutators.forms.processing.error(null);
        postObjectJson(`/merchants/${token.merchant.id}/billings/payment`, {
            subscription_plan: props.view.plan,
            create_msa_application: props.view.createMSAApplication,
            include_website: props.view.includeWebsite,
            desired_domain: props.view.includeWebsite ? props.view.desiredDomain.trim() : '',
        }, {
            'success': (res) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                paymentIdRef.current = (_a = res === null || res === void 0 ? void 0 : res.payment_id) !== null && _a !== void 0 ? _a : null;
                if (res === null || res === void 0 ? void 0 : res.redirect_url)
                    window.open(res.redirect_url, '_blank');
                if (!!pollRef.current)
                    clearInterval(pollRef.current);
                pollRef.current = setInterval(pollForPaymentProcessing, POLL_INTERVAL_MS);
                void pollForPaymentProcessing();
                timeoutRef.current = setTimeout(() => {
                    if (pollRef.current)
                        clearInterval(pollRef.current);
                    mutators.forms.processing.isLoading(false);
                    mutators.forms.processing.error([
                        { field: '', message: 'Payment processing timed out. Please try again or contact support for assistance.' },
                    ]);
                }, PAYMENT_TIMEOUT_MS);
            }),
            'error': (res) => __awaiter(void 0, void 0, void 0, function* () {
                const errorData = yield res.json();
                mutators.forms.processing.isLoading(false);
                mutators.forms.processing.error([{ field: '', message: (errorData === null || errorData === void 0 ? void 0 : errorData.message) || 'Payment failed' }]);
                scrollToTop();
            }),
        });
        return () => {
            if (pollRef.current)
                clearInterval(pollRef.current);
            if (timeoutRef.current)
                clearTimeout(timeoutRef.current);
        };
    }, []);
    return (_jsxs("div", { className: "d-flex flex-column gap-4 vh-100", children: [_jsxs("div", { className: "text-center", children: [_jsx("h1", { children: "Make a Payment" }), _jsx("p", { children: "A new tab to iPosPays Checkout will open. Fill in and submit your payment. Then, return to this page to access your dashboard." })] }), state.forms.processing.isLoading && (_jsxs("div", { className: "text-center m-2", children: [_jsx("div", { className: "spinner-border text-primary", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }), _jsx("p", { className: "mt-2", children: "Waiting for payment to be processed..." })] })), !state.forms.processing.isLoading && state.forms.processing.error && (_jsxs("div", { className: "text-danger bg-danger-subtle p-3 rounded-4", children: [_jsx(CErrors, { errors: state.forms.processing.error, mapping: {} }), _jsxs("div", { className: "mt-3 d-flex justify-content-center gap-2", children: [_jsx("button", { type: "button", className: "btn btn-secondary", onClick: () => props.onNavigateToStep(1), children: "Try again" }), _jsx("button", { type: "button", className: "btn btn-link", onClick: () => props.onNavigateToStep(0), children: "Change plan" })] })] }))] }));
};
